import React from 'react'
import { makeStyles } from '@mui/styles'
import classNames from 'classnames'
import * as R from 'ramda'

import { ClassesType } from '../types'
import keyInsensitiveHighlight from '../utils/keyInsensitiveHighlight'

const useStyles = makeStyles(
  theme => ({
    root: {
      whiteSpace: 'pre-wrap',
      position: 'relative',
      zIndex: theme.utils.modifyZIndex(theme.zIndex.base, 'above'),
    },
    text: {
      color: theme.colors.markerHighlighted,
    },
    highlight: {
      fontWeight: 500,
      zIndex: theme.zIndex.base,
    },
    highlightOnPage: {
      fontWeight: 500,
      backgroundColor: 'rgba(90,152,177,0.30)',
      borderRadius: 4,
      padding: '0 1px',
      margin: '0 -1px',
      zIndex: theme.zIndex.base,
    },
    highlightOnPageFirst: {
      marginLeft: 0,
    },
    highlightOnPageLast: {
      marginRight: 0,
    },
  }),
  { name: 'PuiHighlight' },
)

export interface PuiHighlightProps {
  readonly children?: React.ReactNode
  readonly classes?: ClassesType<typeof useStyles>
  readonly page?: boolean
  readonly searchTerm?: string
}

const PuiHighlight = ({
  searchTerm = '',
  children,
  page,
  classes: classesProp,
}: PuiHighlightProps) => {
  const classes = useStyles({ classes: classesProp })

  if (!searchTerm) {
    return (
      // children can be an array, and we can not return an array from render
      // this is not good both for TS and React
      // eslint-disable-next-line react/jsx-no-useless-fragment
      <>{children}</>
    )
  }

  const childrenArr = React.Children.toArray(children)

  const parseChild = (child: (typeof childrenArr)[number]): any[] => {
    if (R.is(String, child)) {
      return keyInsensitiveHighlight(child, searchTerm)
    }

    if (R.is(Number, child)) {
      return keyInsensitiveHighlight(child.toString(), searchTerm)
    }

    if ('props' in child && child?.props?.children) {
      return React.Children.toArray(child.props.children).map(parseChild)
    }

    return [{ text: child, bold: false }]
  }

  const sliceGroups = childrenArr.map(parseChild)
  const slices = R.flatten(sliceGroups)

  const getHighlightStyle = (index: number) =>
    page
      ? classNames(
        classes.highlightOnPage,
        index === 0 && classes.highlightOnPageFirst,
        index === slices.length - 1 && classes.highlightOnPageLast,
      )
      : classes.highlight

  return (
    <>
      {slices.map(({ text, bold }, index) => (
        <span
          className={classNames(
            classes.root,
            !page && classes.text,
            bold && getHighlightStyle(index),
          )}
          // eslint-disable-next-line react/no-array-index-key
          key={index + text}
        >
          {text}
        </span>
      ))}
    </>
  )
}

export default PuiHighlight
